import { cn } from '@/utils/cn';

export interface BaytarcLogoProps {
  /** Wordmark color — match to the surface it sits on. Icon colors never change. */
  variant?: 'navy' | 'white';
  /** Render just the arch/keyhole mark, no wordmark (favicon, collapsed sidebar). */
  iconOnly?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const ICON_PX = { sm: 24, md: 32, lg: 44 };
const TEXT_CLASS = { sm: 'text-base', md: 'text-xl', lg: 'text-2xl' };

/**
 * The Baytarc "keyhole" mark: a red exterior arch (unified foundation), a navy
 * inner arch (secure domain), and a gold keyhole (premium access) — per the
 * brand identity board in docs/assets/. Icon colors are fixed regardless of
 * surface; only the wordmark color changes via `variant`.
 */
export const BaytarcLogo = ({
  variant = 'navy',
  iconOnly = false,
  size = 'md',
  className,
}: BaytarcLogoProps) => {
  const px = ICON_PX[size];

  return (
    <span className={cn('inline-flex items-center gap-2.5', className)}>
      <svg
        width={px}
        height={px}
        viewBox="0 0 100 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden={!iconOnly}
        role={iconOnly ? 'img' : undefined}
        aria-label={iconOnly ? 'Baytarc' : undefined}
      >
        <path d="M18 88 L18 48 A32 32 0 0 1 82 48 L82 88 Z" fill="#c10007" />
        <path d="M27 86 L27 49 A23 23 0 0 1 73 49 L73 86 Z" fill="#101828" />
        <circle cx="50" cy="59" r="6.5" fill="#ffd700" />
        <path d="M45.5 61 L54.5 61 L51 78 L49 78 Z" fill="#ffd700" />
      </svg>
      {!iconOnly && (
        <span
          className={cn(
            'font-display font-bold tracking-tight',
            TEXT_CLASS[size],
            variant === 'white' ? 'text-white' : 'text-secondary-900'
          )}
        >
          Baytarc
        </span>
      )}
    </span>
  );
};

export default BaytarcLogo;
